// apps/provider/src/hooks/useCalendarAlerts.js
// Consolidated alert layer for the calendar: billing-rule violations,
// prior-auth alerts, POC-expiration alerts, org payer alerts, and persisted
// form answer alerts (patient_alert rows).
//
// Replaces useCalendarBillingAlerts + the three inline effects that lived in
// AppointmentCalendar. All fetching/dedup/freshness lives in
// calendarAlertStore (delta SWR — only missing/stale ids are requested, so
// remounts and back-navigation are free); this hook computes the visible id
// sets, triggers ensure*, and derives the per-appointment alert maps.

import { useEffect, useMemo, useState } from 'react';
import { useUser } from '../contexts/UserContext';
import { useCalendarAlertStore } from '../stores/calendarAlertStore';
import {
  evaluateBillingRules,
  derivePocCertificationDate,
  derivePocCertificationEnd,
  derivePayerContext,
} from '@prism-ehr/shared/utils/billingRuleEngine';
import { derivePnDue, isPocExpirationViolation } from '../utils/pnDue';

// PN-due calendar color: the predicate lives in utils/pnDue.js and is shared
// with CreateVisitModal (forced note type) so the two surfaces agree.
//
// Target placement, default (any note satisfies the milestone): the first
// appointment of the case that is neither terminal nor already has an
// encounter. The visit count behind the rule includes every non-cancelled
// encounter row, and an encounter is created the moment a note is started,
// so an appointment with an encounter_id is already the last counted visit
// and the violation describes the one after it.
//
// Signed-only orgs (settings.documentation.pn_due_signed_only): the milestone
// stays due while the note is a draft, so the target is simply the first
// non-terminal appointment, encounter or not. Signing auto-completes the
// linked appointment (clinical_note sign endpoint), which moves the target.
const COMPLETED_STATUSES = ['completed', 'documented', 'cancelled', 'no_show'];
const firstIncompleteAppointment = (caseApts, { ignoreEncounter = false } = {}) => {
  const sorted = [...caseApts].sort((a, b) =>
    (a.appointment_date || '').localeCompare(b.appointment_date || '') ||
    (a.start_time || '').localeCompare(b.start_time || '')
  );
  return sorted.find(a =>
    !COMPLETED_STATUSES.includes(a.status) && (ignoreEncounter || !a.encounter_id)
  ) || null;
};

/**
 * @param {{ appointments: Array, organizationId: string, billingMode: string, withUpcomingCounts?: boolean }} options
 *  withUpcomingCounts: also fetch per-patient upcoming-appointment counts
 *  (Home schedule chip); off by default so the calendar pays nothing for it.
 *  withVisitLimits: also fetch low insurance benefit visit limits per patient
 *  (Home / calendar chip); off by default, skipped in cash mode.
 * @returns {{ billingAlertMap: Object, priorAuthAlertMap: Object, pocAlertMap: Object, orgPayerAlertMap: Object, upcomingCountMap: Object, formAlertMap: Object }}
 *  billingAlertMap: appointment id -> { message, pn_due, hard_block };
 *  priorAuthAlertMap: patient id -> { message, used, authorized, payer, ... }; pocAlertMap: case id -> message;
 *  orgPayerAlertMap: claimmd_payer_id -> message;
 *  upcomingCountMap: patient id -> upcoming appointment count (empty unless withUpcomingCounts);
 *  formAlertMap: patient id -> unresolved calendar-visible patient_alert rows (non-empty only);
 *  visitLimitAlertMap: patient id -> low benefit-limit rows (non-empty only, empty unless withVisitLimits).
 */
export function useCalendarAlerts({ appointments, organizationId, billingMode, withUpcomingCounts = false, withVisitLimits = false }) {
  const { user } = useUser();
  // Org setting: only SIGNED progress/reassessment notes satisfy a milestone
  // (see utils/pnDue.js header and buildCaseEvaluationContext)
  const signedOnly = !!user?.organization?.settings?.documentation?.pn_due_signed_only;
  // Org setting: POC certification alerts only for Medicare/MA coverage
  // (see utils/pnDue.js header). The store stamps `medicare` per case.
  const pocMedicareOnly = !!user?.organization?.settings?.documentation?.poc_alerts_medicare_only;
  const billingRules = useCalendarAlertStore(s => s.billingRules);
  const billingContextByCase = useCalendarAlertStore(s => s.billingContextByCase);
  const priorAuthByPatient = useCalendarAlertStore(s => s.priorAuthByPatient);
  const pocByCase = useCalendarAlertStore(s => s.pocByCase);
  const upcomingByPatient = useCalendarAlertStore(s => s.upcomingByPatient);
  const formAlertsByPatient = useCalendarAlertStore(s => s.formAlertsByPatient);
  const visitLimitsByPatient = useCalendarAlertStore(s => s.visitLimitsByPatient);
  const orgPayerAlertMap = useCalendarAlertStore(s => s.payerAlertMap);
  const orgPayerAlertMapById = useCalendarAlertStore(s => s.payerAlertMapById);
  const payerRoster = useCalendarAlertStore(s => s.payerRoster);
  const payerGroupsById = useCalendarAlertStore(s => s.payerGroupsById);

  // Hidden tabs fetch no alerts (2026-09-22): a realtime status change
  // invalidates the touched case/patient in every tab, and 40 hidden calendars
  // refetching the four alert batches together was the second wave of the
  // 9/22 outage. The effect re-runs when the tab becomes visible.
  const [visibleTick, setVisibleTick] = useState(0);
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible') setVisibleTick(t => t + 1);
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  // Keep the caches covering the visible ids — the store only fetches
  // missing/stale entries, so identical id sets cost nothing
  useEffect(() => {
    if (!appointments?.length || !organizationId) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    const store = useCalendarAlertStore.getState();
    // Exclude 0 as well as null/undefined — legacy rows default absent case_id
    // to 0, and orphan POCs grouped under key 0 leak alerts across patients
    const caseIds = [...new Set(appointments.map(a => a.case_id).filter(id => id != null && id !== 0))];
    const patientIds = [...new Set(appointments.map(a => a.patient_id).filter(Boolean))];

    store.ensurePayers();
    if (caseIds.length) {
      store.ensureBillingContext({ caseIds, organizationId });
    }
    // Cash-mode orgs have no prior-auth or POC-certification workflow (policy lives here, not in the store)
    if (billingMode !== 'cash') {
      if (caseIds.length) store.ensurePoc({ caseIds });
      if (patientIds.length) store.ensurePriorAuth({ patientIds });
      if (withVisitLimits && patientIds.length) store.ensureVisitLimits({ patientIds });
    }
    // Upcoming counts are a scheduling concern, not a billing one: no billingMode gate
    if (withUpcomingCounts && patientIds.length) store.ensureUpcomingCounts({ patientIds });
    // Form answer alerts are a clinical concern, not a billing one: no billingMode gate
    if (patientIds.length) store.ensureFormAlerts({ patientIds });
  }, [appointments, organizationId, billingMode, withUpcomingCounts, withVisitLimits, visibleTick]);

  // Billing-rule evaluation over cached raw context. Placement logic ported
  // from useCalendarBillingAlerts: visit_milestone alerts land only on the
  // first upcoming incomplete appointment of the case; persistent alerts on
  // every appointment of the case. Because this derives from live appointment
  // statuses, alerts re-place when a status changes mid-session.
  const billingAlertMap = useMemo(() => {
    if (!appointments?.length) return {};
    const relevantRules = (billingRules || []).filter(r =>
      r.category === 'visit_milestone' || r.category === 'documentation_trigger'
    );
    const placement = { ignoreEncounter: signedOnly };
    const pocApplies = (caseId) => !pocMedicareOnly || !!pocByCase[caseId]?.medicare;

    const caseAppointments = {};
    for (const apt of appointments) {
      if (!apt.case_id) continue;
      if (!caseAppointments[apt.case_id]) caseAppointments[apt.case_id] = [];
      caseAppointments[apt.case_id].push(apt);
    }

    const alertMap = {};
    const setEntry = (aptId, patch) => {
      const prev = alertMap[aptId] || { message: '', pn_due: false, hard_block: false };
      alertMap[aptId] = { ...prev, ...patch };
    };

    // Expired POC (from the POC records themselves) colors the case's first
    // incomplete appointment even when the org has no billing rules loaded.
    // derivePnDue applies the non-cash billing-mode gate.
    for (const [caseId, caseApts] of Object.entries(caseAppointments)) {
      const { pnDue } = derivePnDue({ pocExpired: pocApplies(caseId) && !!pocByCase[caseId]?.expired, billingMode });
      if (!pnDue) continue;
      const target = firstIncompleteAppointment(caseApts, placement);
      if (target) setEntry(target.id, { pn_due: true });
    }

    if (relevantRules.length === 0) return alertMap;

    for (const [caseId, caseApts] of Object.entries(caseAppointments)) {
      const context = billingContextByCase[caseId];
      if (!context) continue;

      // Discharged/held cases don't need milestone or recert alerts. Filter
      // only when the backend supplies case status so cached entries from
      // before the status field existed behave as they did.
      if (context.caseRecord?.status && context.caseRecord.status !== 'active') continue;

      const sampleApt = caseApts[0];
      const patient = sampleApt.patient || sampleApt._patient;

      // Assemble the evaluation context directly from the endpoint's per-case
      // SQL aggregates (v2 contract) — the raw-row reduction now lives in the
      // database. A case with no non-cancelled encounters has aggregate: null.
      const agg = context.aggregate;
      const num = (v) => (v == null ? undefined : Number(v));
      // Signed-only orgs read the last_signed_* aggregate columns. Selected
      // by KEY PRESENCE (the signed visit is legitimately null when no signed
      // note exists); an older endpoint without the keys degrades to the
      // any-note columns, i.e. today's behavior, never to "never satisfied".
      const useSigned = signedOnly && !!agg && ('last_signed_progress_note_visit' in agg);
      const lastPnDate = useSigned ? agg.last_signed_progress_note_date : agg?.last_progress_note_date;
      const lastPnVisit = useSigned ? agg.last_signed_progress_note_visit : agg?.last_progress_note_visit;
      const evalContext = {
        clinical_specialty: context.caseRecord?.clinical_specialty || sampleApt.clinical_specialty,
        care_setting: context.caseRecord?.care_setting,
        visit_history: {
          total_visits: agg ? Number(agg.total_visits) : 0,
          current_visit_number: agg ? Number(agg.current_visit_number) : 0,
          last_progress_note_date: num(lastPnDate),
          last_progress_note_visit: num(lastPnVisit),
          last_encounter_date: num(agg?.last_encounter_date),
          poc_certification_date:
            derivePocCertificationDate(context.plans_of_care, parseInt(caseId)) ?? undefined,
          poc_certification_end:
            derivePocCertificationEnd(context.plans_of_care, parseInt(caseId)) ?? undefined,
          milestone_requires_signed: signedOnly || undefined,
        },
        payer: derivePayerContext(patient),
      };

      const result = evaluateBillingRules(
        relevantRules,
        evalContext,
        ['visit_milestone', 'documentation_trigger']
      );
      // Medicare-only POC alerts: drop poc_expiration firings for other coverage
      const violations = pocApplies(caseId)
        ? result.violations
        : result.violations.filter(v => !isPocExpirationViolation(v));
      if (violations.length === 0) continue;

      const visitViolations = violations.filter(v => v.category === 'visit_milestone');
      const persistentViolations = violations.filter(v => v.category !== 'visit_milestone');

      const persistentMsg = persistentViolations
        .map(v => v.message || v.rule_name)
        .join('; ');
      if (persistentMsg) {
        for (const apt of caseApts) {
          setEntry(apt.id, { message: persistentMsg });
        }
      }

      // PN-due flag (utils/pnDue.js) marks the TARGET (first incomplete)
      // appointment; hard_block when any such violation is hard_block.
      // Messages keep their existing placement.
      const { pnDue, hardBlock: pnHardBlock } = derivePnDue({ violations, billingMode });

      if (visitViolations.length > 0 || pnDue) {
        const target = firstIncompleteAppointment(caseApts, placement);
        if (target) {
          if (visitViolations.length > 0) {
            const visitMsg = visitViolations
              .map(v => v.message || v.rule_name)
              .join('; ');
            const existing = alertMap[target.id]?.message;
            setEntry(target.id, { message: existing ? `${existing}; ${visitMsg}` : visitMsg });
          }
          if (pnDue) {
            setEntry(target.id, { pn_due: true, hard_block: pnHardBlock || !!alertMap[target.id]?.hard_block });
          }
        }
      }
    }
    return alertMap;
  }, [appointments, billingRules, billingContextByCase, pocByCase, billingMode, signedOnly, pocMedicareOnly]);

  const priorAuthAlertMap = useMemo(() => {
    if (billingMode === 'cash') return {};
    const map = {};
    for (const [patientId, entry] of Object.entries(priorAuthByPatient)) {
      if (entry?.violation) map[patientId] = entry.violation;
    }
    return map;
  }, [priorAuthByPatient, billingMode]);

  const pocAlertMap = useMemo(() => {
    if (billingMode === 'cash') return {};
    const map = {};
    for (const [caseId, entry] of Object.entries(pocByCase)) {
      if (pocMedicareOnly && !entry?.medicare) continue;
      if (entry?.violation) map[caseId] = entry.violation;
    }
    return map;
  }, [pocByCase, billingMode, pocMedicareOnly]);

  const upcomingCountMap = useMemo(() => {
    if (!withUpcomingCounts) return {};
    const map = {};
    for (const [patientId, entry] of Object.entries(upcomingByPatient)) {
      if (typeof entry?.count === 'number') map[patientId] = entry.count;
    }
    return map;
  }, [upcomingByPatient, withUpcomingCounts]);

  const formAlertMap = useMemo(() => {
    const map = {};
    for (const [patientId, entry] of Object.entries(formAlertsByPatient)) {
      if (Array.isArray(entry?.alerts) && entry.alerts.length > 0) map[patientId] = entry.alerts;
    }
    return map;
  }, [formAlertsByPatient]);

  const visitLimitAlertMap = useMemo(() => {
    if (!withVisitLimits || billingMode === 'cash') return {};
    const map = {};
    for (const [patientId, entry] of Object.entries(visitLimitsByPatient)) {
      if (Array.isArray(entry?.items) && entry.items.length > 0) map[patientId] = entry.items;
    }
    return map;
  }, [visitLimitsByPatient, withVisitLimits, billingMode]);

  return { billingAlertMap, priorAuthAlertMap, pocAlertMap, orgPayerAlertMap, orgPayerAlertMapById, payerRoster, payerGroupsById, upcomingCountMap, formAlertMap, visitLimitAlertMap };
}
